// LogStatistics.tsx
import { useState, useCallback, useEffect } from "react";
import { RefreshCw, Loader2, Info, AlertTriangle } from "lucide-react";
import {
    BarChart, Bar, ResponsiveContainer, Tooltip, Cell
} from "recharts";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface DailyCount {
    day: string;
    date: string;
    count: number;
}

interface StatEntry {
    label: string;
    total_7day: number;
    description: string;
    daily_counts: DailyCount[];
}

interface Stats7Day {
    traffic: StatEntry;
    "app-ctrl": StatEntry;
    event: StatEntry;
    virus: StatEntry;
    ssl: StatEntry;
    ips: StatEntry;
}

interface FazSetting {
    status: string;
    "mgmt-name": string;
    "upload-interval": string;
    "upload-option": string;
    reliable: string;
    "enc-algorithm": string;
}

interface RecentLogs {
    note: string;
    [key: string]: any;
}

interface LogStatsData {
    stats_7day?: Partial<Stats7Day> | null;
    faz_setting?: Partial<FazSetting> | null;
    recent_logs?: RecentLogs | null;
    disk_used_pct?: number;
    faz_connected?: number;
    disk_used_bytes?: number;
    faz_cached_logs?: number;
    faz_failed_logs?: number;
    disk_total_bytes?: number;
}

// ── Config ────────────────────────────────────────────────────────────────────
const STAT_CONFIG: {
    key: keyof Stats7Day;
    shortLabel: string;
    color: string;
    barColor: string;
    hoverColor: string;
}[] = [
        { key: "traffic", shortLabel: "Traffic logs", color: "text-blue-400", barColor: "#3b82f6", hoverColor: "#60a5fa" },
        { key: "app-ctrl", shortLabel: "App control", color: "text-green-400", barColor: "#22c55e", hoverColor: "#4ade80" },
        { key: "event", shortLabel: "Event logs", color: "text-purple-400", barColor: "#a855f7", hoverColor: "#c084fc" },
        { key: "virus", shortLabel: "Virus / malware", color: "text-red-400", barColor: "#ef4444", hoverColor: "#f87171" },
        { key: "ssl", shortLabel: "SSL inspection", color: "text-yellow-400", barColor: "#eab308", hoverColor: "#facc15" },
        { key: "ips", shortLabel: "IPS events", color: "text-orange-400", barColor: "#f97316", hoverColor: "#fb923c" },
    ];

// ── Defaults ──────────────────────────────────────────────────────────────────
const EMPTY_STAT_ENTRY: StatEntry = {
    label: "",
    total_7day: 0,
    description: "",
    daily_counts: [],
};

const EMPTY_STATS_7DAY: Stats7Day = {
    traffic: { ...EMPTY_STAT_ENTRY, label: "traffic" },
    "app-ctrl": { ...EMPTY_STAT_ENTRY, label: "app-ctrl" },
    event: { ...EMPTY_STAT_ENTRY, label: "event" },
    virus: { ...EMPTY_STAT_ENTRY, label: "virus" },
    ssl: { ...EMPTY_STAT_ENTRY, label: "ssl" },
    ips: { ...EMPTY_STAT_ENTRY, label: "ips" },
};

const EMPTY_FAZ_SETTING: FazSetting = {
    status: "",
    "mgmt-name": "",
    "upload-interval": "",
    "upload-option": "",
    reliable: "",
    "enc-algorithm": "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtFull = (n: number): string => n.toLocaleString();

const shortDate = (dateStr: string): string => {
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${month}`;
};

const normalizeStatEntry = (entry?: Partial<StatEntry> | null, fallbackLabel = ""): StatEntry => ({
    label: entry?.label || fallbackLabel,
    total_7day: typeof entry?.total_7day === "number" ? entry.total_7day : 0,
    description: entry?.description || "",
    daily_counts: Array.isArray(entry?.daily_counts) ? entry!.daily_counts!.filter(Boolean) : [],
});

const normalizeStats7Day = (stats?: Partial<Stats7Day> | null): Stats7Day => ({
    traffic: normalizeStatEntry(stats?.traffic, "traffic"),
    "app-ctrl": normalizeStatEntry(stats?.["app-ctrl"], "app-ctrl"),
    event: normalizeStatEntry(stats?.event, "event"),
    virus: normalizeStatEntry(stats?.virus, "virus"),
    ssl: normalizeStatEntry(stats?.ssl, "ssl"),
    ips: normalizeStatEntry(stats?.ips, "ips"),
});

const hasAnyStats = (stats: Stats7Day): boolean =>
    Object.values(stats).some(
        entry => entry.total_7day > 0 || (entry.daily_counts?.length ?? 0) > 0
    );

// ── Custom Tooltip ────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, color }: any) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 shadow-xl">
            <p className={`text-[12px] font-black ${color}`}>{fmtFull(payload[0].value)}</p>
        </div>
    );
};

// ── Mini Bar Chart Card ───────────────────────────────────────────────────────
const StatCard = ({
    entry,
    shortLabel,
    color,
    barColor,
    hoverColor,
}: {
    entry: StatEntry;
    shortLabel: string;
    color: string;
    barColor: string;
    hoverColor: string;
}) => {
    const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);

    const chartData = [...(entry.daily_counts || [])].reverse().map(d => ({
        date: shortDate(d.date),
        count: d.count ?? 0,
        fullDate: d.date,
    }));

    const hasChartData = chartData.length > 0;

    return (
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-800 rounded-xl p-4 flex flex-col gap-3 shadow-sm">
            <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-700 dark:text-gray-200 font-medium">{shortLabel}</span>
            </div>

            <div>
                <span className={`text-[28px] font-black leading-none tracking-tight ${color}`}>
                    {(entry.total_7day ?? 0).toLocaleString()}
                </span>
            </div>

            <div className="h-[70px] w-full">
                {hasChartData ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={chartData}
                            barCategoryGap="20%"
                            margin={{ top: 2, right: 0, left: 0, bottom: 0 }}
                        >
                            <Tooltip
                                content={<CustomTooltip color={color} />}
                                cursor={false}
                            />
                            <Bar
                                dataKey="count"
                                radius={[2, 2, 0, 0]}
                                onMouseEnter={(_, idx) => setHoveredIdx(idx)}
                                onMouseLeave={() => setHoveredIdx(null)}
                            >
                                {chartData.map((_, idx) => (
                                    <Cell
                                        key={idx}
                                        fill={hoveredIdx === idx ? hoverColor : barColor}
                                        opacity={hoveredIdx !== null && hoveredIdx !== idx ? 0.5 : 1}
                                    />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="h-full flex items-center justify-center rounded-lg border border-dashed border-slate-200 dark:border-gray-700">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 dark:text-gray-200/40">
                            No data
                        </span>
                    </div>
                )}
            </div>

            <div className="flex justify-between px-0 min-h-[12px]">
                {hasChartData ? (
                    chartData.map((d, i) => (
                        <span key={i} className="text-[8px] text-slate-500 dark:text-gray-200 font-mono w-full text-center">
                            {d.date}
                        </span>
                    ))
                ) : (
                    <span className="text-[8px] text-slate-400 dark:text-gray-200/30 font-mono w-full text-center">—</span>
                )}
            </div>
        </div>
    );
};

// ── Daily Breakdown Table ─────────────────────────────────────────────────────
const DailyTable = ({ stats }: { stats: Stats7Day }) => {
    const dates = [...(stats.traffic.daily_counts || [])]
        .reverse()
        .map(d => d.date);

    const getCount = (key: keyof Stats7Day, date: string): number =>
        stats[key].daily_counts.find(d => d.date === date)?.count ?? 0;

    const formatDate = (dateStr: string): string => {
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    };

    const COL_COLORS: Record<string, string> = {
        traffic: "text-blue-600 dark:text-blue-400",
        "app-ctrl": "text-emerald-600 dark:text-green-400",
        event: "text-purple-600 dark:text-purple-400",
        virus: "text-red-600 dark:text-red-400",
        ssl: "text-yellow-600 dark:text-yellow-400",
        ips: "text-orange-600 dark:text-orange-400",
    };

    return (
        <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-slate-200 dark:border-gray-800">
                <h3 className="text-[13px] font-bold text-gray-900 dark:text-white">Daily breakdown table</h3>
            </div>

            {dates.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-12 opacity-60 dark:opacity-40">
                    <Info size={18} className="text-slate-400 dark:text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">
                        No daily breakdown available
                    </span>
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-gray-900/40 border-b border-slate-200 dark:border-gray-700">
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">Date</th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">Traffic</th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">App Control</th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">Events</th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">Virus</th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">SSL</th>
                                <th className="px-4 py-2.5 text-[10px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">IPS</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/60">
                            {dates.map(date => (
                                <tr key={date} className="hover:bg-slate-50 dark:hover:bg-gray-800/30 transition-colors">
                                    <td className="px-4 py-2.5 text-[11px] font-bold text-slate-800 dark:text-gray-300 whitespace-nowrap">
                                        {formatDate(date)}
                                    </td>
                                    {(["traffic", "app-ctrl", "event", "virus", "ssl", "ips"] as (keyof Stats7Day)[]).map(key => {
                                        const val = getCount(key, date);
                                        return (
                                            <td
                                                key={key}
                                                className={`px-4 py-2.5 text-[11px] font-bold whitespace-nowrap ${val === 0 ? "text-slate-400 dark:text-gray-200" : COL_COLORS[key]
                                                    }`}
                                            >
                                                {val === 0 ? "0" : val.toLocaleString()}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function LogStatistics({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [data, setData] = useState<LogStatsData | null>(null);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "log_statistics",
                    }),
                    limit: 1,
                },
            });

            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setData(parsed ?? null);
            } else {
                setData(null);
                showError("Failed to load log statistics");
            }
        } catch (e: any) {
            setData(null);
            showError(e.message);
        } finally {
            setLoading(false);
        }
    }, [asset_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    if (loading) return (
        <div className="flex items-center justify-center gap-2 py-20">
            <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">
                Loading log statistics...
            </span>
        </div>
    );

    if (!data) return (
        <div className="flex flex-col items-center gap-2 py-16 opacity-60 dark:opacity-40">
            <Info size={22} className="text-slate-400 dark:text-gray-200" />
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">
                No log data available
            </span>
        </div>
    );

    const safeStats = normalizeStats7Day(data.stats_7day);
    const faz = {
        ...EMPTY_FAZ_SETTING,
        ...(data.faz_setting || {}),
    };

    const hasStats = hasAnyStats(safeStats);

    const allDates = (safeStats.traffic.daily_counts || [])
        .map(d => d.date)
        .filter(Boolean)
        .sort();

    const dateRange = allDates.length >= 2
        ? `${allDates[0]} to ${allDates[allDates.length - 1]}`
        : allDates.length === 1
            ? allDates[0]
            : "No date range";

    return (
        <div className="space-y-4">

            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[11px] text-slate-600 dark:text-gray-200 mt-0.5">
                        7-day counts from FortiGate log engine · {dateRange}
                    </p>
                </div>
                <button
                    onClick={fetchData}
                    className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group"
                >
                    <RefreshCw size={14} className="group-hover:text-blue-500" />
                </button>
            </div>

            {/* No-disk notice */}
            {data.recent_logs?.note && (
                <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded-lg">
                    <AlertTriangle size={13} className="text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[10px] text-amber-600 dark:text-amber-400/80">{data.recent_logs.note}</p>
                </div>
            )}

            {/* Stats cards */}
            {hasStats ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    {STAT_CONFIG.map(cfg => (
                        <StatCard
                            key={cfg.key}
                            entry={safeStats[cfg.key] || EMPTY_STATS_7DAY[cfg.key]}
                            shortLabel={cfg.shortLabel}
                            color={cfg.color}
                            barColor={cfg.barColor}
                            hoverColor={cfg.hoverColor}
                        />
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center gap-2 py-16 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-xl opacity-60 dark:opacity-50">
                    <Info size={20} className="text-slate-400 dark:text-gray-200" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">
                        No 7-day statistics available
                    </span>
                </div>
            )}

            {/* Daily breakdown */}
            <DailyTable stats={safeStats} />

            {/* FAZ Settings footer */}
            <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-800 rounded-xl px-4 py-3 shadow-sm">
                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest mb-2">
                    FortiAnalyzer Settings
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                    {[
                        { label: "Status", value: faz.status },
                        { label: "Mgmt Name", value: faz["mgmt-name"] },
                        { label: "Upload Interval", value: faz["upload-interval"] },
                        { label: "Upload Option", value: faz["upload-option"] },
                        { label: "Reliable", value: faz.reliable },
                        { label: "Encryption", value: faz["enc-algorithm"] },
                    ].map(({ label, value }) => (
                        <div key={label} className="flex flex-col gap-0.5">
                            <span className="text-[8px] font-bold text-slate-500 dark:text-gray-200 uppercase tracking-widest">
                                {label}
                            </span>
                            <span
                                className={`text-[10px] font-bold ${value === "enable"
                                    ? "text-emerald-600 dark:text-green-400"
                                    : value === "disable"
                                        ? "text-slate-400 dark:text-gray-200"
                                        : "text-slate-700 dark:text-gray-300"
                                    }`}
                            >
                                {value || "—"}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}